// Single source of truth for CSRF tokens + JSON fetch. ALWAYS use apiFetch (or, for
// Inertia router calls that pass an explicit header, csrfToken) — never read the
// `meta[name="csrf-token"]` tag directly in a component.
//
// Why: the meta tag is rendered ONCE at the tab's initial full-page load and never
// updates in the Inertia SPA. When the session rotates (Devise sign-in/out in any tab,
// session expiry), that frozen token starts failing every mutating request with
// InvalidAuthenticityToken — a long-lived calendar tab silently loses the user's work.
// The XSRF-TOKEN cookie, by contrast, is refreshed by inertia_rails on every response
// from any tab, so reading it at request time always yields the current token.

export function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  if (match) return decodeURIComponent(match[1])
  // Fallback for the first-ever request of a fresh browser (no cookie set yet).
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Your session was refreshed — please try again.')
    this.name = 'SessionExpiredError'
  }
}

/** Returns the user-facing message for a SessionExpiredError, or the fallback for anything else. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof SessionExpiredError ? error.message : fallback
}

// fetch() with JSON + CSRF headers attached at request time. A CSRF rejection renders the
// static HTML 422 page (app validation 422s are JSON) — on that signature we retry once
// with a freshly-read token, then surface a friendly SessionExpiredError.
export async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const doFetch = () =>
    fetch(url, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers || {}), 'X-CSRF-Token': csrfToken() },
    })

  let response = await doFetch()
  if (isCsrfRejection(response)) {
    response = await doFetch()
    if (isCsrfRejection(response)) throw new SessionExpiredError()
  }
  return response
}

function isCsrfRejection(response: Response): boolean {
  return response.status === 422 && !(response.headers.get('content-type') || '').includes('json')
}
